import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import {
  registerUserApi,
  loginUserApi,
  getUserProfileApi,
  updateUserProfileApi,
  addAddressApi,
  deleteAddressApi,
  trackRestaurantVisitApi,
  getVisitedRestaurantsApi,
} from "../../api/userApi";

export const registerUser = createAsyncThunk(
  "user/register",
  async (userData, { rejectWithValue }) => {
    try {
      const data = await registerUserApi(userData);
      localStorage.setItem("userAccessToken", data.accessToken);
      localStorage.setItem("userRefreshToken", data.refreshToken);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Registration failed"
      );
    }
  }
);

export const loginUser = createAsyncThunk(
  "user/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const data = await loginUserApi(credentials);
      localStorage.setItem("userAccessToken", data.accessToken);
      localStorage.setItem("userRefreshToken", data.refreshToken);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Login failed"
      );
    }
  }
);

export const getUserProfile = createAsyncThunk(
  "user/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      return await getUserProfileApi();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch profile"
      );
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "user/updateProfile",
  async (updates, { rejectWithValue }) => {
    try {
      return await updateUserProfileApi(updates);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Update failed"
      );
    }
  }
);

export const addAddress = createAsyncThunk(
  "user/addAddress",
  async (addressData, { rejectWithValue }) => {
    try {
      return await addAddressApi(addressData);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add address"
      );
    }
  }
);

export const deleteAddress = createAsyncThunk(
  "user/deleteAddress",
  async (addressId, { rejectWithValue }) => {
    try {
      return await deleteAddressApi(addressId);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete address"
      );
    }
  }
);

export const trackRestaurantVisit = createAsyncThunk(
  "user/trackVisit",
  async (restaurantId, { rejectWithValue }) => {
    try {
      return await trackRestaurantVisitApi(restaurantId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Tracking failed");
    }
  }
);

export const getVisitedRestaurants = createAsyncThunk(
  "user/getVisitedRestaurants",
  async (_, { rejectWithValue }) => {
    try {
      return await getVisitedRestaurantsApi();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch visited restaurants"
      );
    }
  }
);

// ══════════════════════════════════════════════════════════════════
// SLICE
// ══════════════════════════════════════════════════════════════════

const userSlice = createSlice({
  name: "user",
  initialState: {
    currentUser: null,
    isAuthenticated: false,
    visitedRestaurants: [],
    loading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearUserError: (state) => {
      state.error = null;
    },
    clearUserSuccess: (state) => {
      state.success = null;
    },
    userLogout: (state) => {
      state.currentUser = null;
      state.isAuthenticated = false;
      state.visitedRestaurants = [];
      localStorage.removeItem("userAccessToken");
      localStorage.removeItem("userRefreshToken");
    },
  },
  extraReducers: (builder) => {
    // ── Register ─────────────────────────────────────────────────
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.currentUser = action.payload.user;
        state.success = action.payload.message;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── Login ────────────────────────────────────────────────────
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.currentUser = action.payload.user;
        state.success = action.payload.message;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── Get Profile ──────────────────────────────────────────────
    builder
      .addCase(getUserProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // ── Update Profile ───────────────────────────────────────────
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload.user;
        state.success = action.payload.message;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── Add Address ──────────────────────────────────────────────
    builder
      .addCase(addAddress.pending, (state) => {
        state.loading = true;
      })
      .addCase(addAddress.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentUser) {
          state.currentUser.addresses = action.payload.addresses;
        }
        state.success = action.payload.message;
      })
      .addCase(addAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── Delete Address ───────────────────────────────────────────
    builder
      .addCase(deleteAddress.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentUser) {
          state.currentUser.addresses = action.payload.addresses;
        }
        state.success = action.payload.message;
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── Track Visit ──────────────────────────────────────────────
    builder
      .addCase(trackRestaurantVisit.pending, (state) => {
        // Silent tracking - no loading state
      })
      .addCase(trackRestaurantVisit.fulfilled, (state) => {
        // Success - silently tracked
      })
      .addCase(trackRestaurantVisit.rejected, (state) => {
        // Fail silently
      });

    // ── Get Visited Restaurants ──────────────────────────────────
    builder
      .addCase(getVisitedRestaurants.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVisitedRestaurants.fulfilled, (state, action) => {
        state.loading = false;
        state.visitedRestaurants = action.payload.visitedRestaurants;
      })
      .addCase(getVisitedRestaurants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearUserError, clearUserSuccess, userLogout } =
  userSlice.actions;

export default userSlice.reducer;

// ══════════════════════════════════════════════════════════════════
// SELECTORS
// ══════════════════════════════════════════════════════════════════

export const selectCurrentUser = (state) => state.user.currentUser;
export const selectIsUserAuthenticated = (state) => state.user.isAuthenticated;
export const selectVisitedRestaurants = (state) =>
  state.user.visitedRestaurants;
export const selectUserLoading = (state) => state.user.loading;
export const selectUserError = (state) => state.user.error;
export const selectUserSuccess = (state) => state.user.success;